//Dependencies............................................
import React, {useEffect, useState} from "react";
import axios from 'axios';
import {Button, Collapse, theme, Tooltip,Descriptions,Card, CollapseProps, DescriptionsProps, Space, Tag, Typography} from 'antd';
import {PlusOutlined, EditOutlined, DownloadOutlined} from '@ant-design/icons';
import {
    useQuery
} from '@tanstack/react-query'
import {useForm} from "react-hook-form";

//Components..............................................
import {BASE_URL} from '../../../utils/constants'
import {FirmwareBase, JsonSchemaItem} from "../../../utils/interfaces";

import AddFirmwareBaseListModal from "../../../Components/FirmwareManagerPageComp/Modals/AddFirmwareBaseListModal";
import EditFirmwareBaseListModal from "../../../Components/FirmwareManagerPageComp/Modals/EditFirmwareBaseListModal";
import { NotificationInstance } from "antd/es/notification/interface";
import { useHasAction } from "../../../hooks/useHasAction";
import { PATCH_NOTE_ITEM_META, PatchNoteItemType, parsePatchNotes } from "../../../utils/patchNotes";

axios.defaults.withCredentials = true


type FirmwareBaseListResponse = {
    success: boolean,
    content: FirmwareBase[]
}

type JsonSchemaListResponse = {
    success: boolean,
    content: JsonSchemaItem[]
}

type FirmwareBaseStatesResponse = {
    success: boolean,
    content: string[]
}


const download = (data: FirmwareBase, type: string, extension: string, api: NotificationInstance) => {
    axios({
        url: `${BASE_URL}download_firmware_base/${data.id}/${type}`, //your url
        method: 'GET',
        responseType: 'blob', // important
    }).then((response) => {
        // create file link in browser's memory
        const href = URL.createObjectURL(response.data);

        // create "a" HTML element with href to file & click
        const link = document.createElement('a');
        link.href = href;
        const filename = "CANNY_" + type + "_" + data.version_name + "." + extension

        link.setAttribute('download', filename); //or any other extension
        document.body.appendChild(link);
        link.click();

        // clean up "a" element & remove ObjectURL
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
        api.success({
            title: 'Download Successful',
            description: `Firmware ${type} downloaded successfully.`,
            placement: 'bottomRight',
            showProgress: true
        });
    }).catch((error) => {
        api.error({
            title: 'Download Failed',
            description: `Firmware ${type} failed to download: ${error.message}`,
            placement: 'bottomRight',
            showProgress: true
        });
    });
}

const cardStyle: React.CSSProperties = {
    height: 'calc(100vh - 150px)',
    width: "100%",
    overflow: "hidden scroll"
};

const { Text } = Typography;

const statusColors: Record<string, string> = {
    NEW: "blue",
    TESTING: "gold",
    APPROVED: "cyan",
    RELEASED: "green",
    REJECTED: "red",
};

const getStatusColor = (status: string) => statusColors[status] ?? "default";

const renderChangelog = (changelog: string | undefined) => {
    const notes = parsePatchNotes(changelog);
    if (!notes) {
        return changelog || <Text type="secondary">No changelog provided</Text>;
    }
    return (
        <div>
            <Tag color="blue" style={{ marginBottom: 6 }}>Shown as patch notes on mobile</Tag>
            {notes.summary && <Text style={{ display: "block", marginBottom: 6 }}>{notes.summary}</Text>}
            <ul style={{ margin: 0, paddingLeft: 18 }}>
                {notes.items.filter(item => item.text.trim()).map((item, i) => (
                    <li key={i}>
                        <Text style={{ color: PATCH_NOTE_ITEM_META[item.type as PatchNoteItemType].color, fontWeight: 600 }}>
                            {PATCH_NOTE_ITEM_META[item.type as PatchNoteItemType].icon}{" "}
                        </Text>
                        {item.text}
                    </li>
                ))}
            </ul>
        </div>
    );
};

interface Props {
    api: NotificationInstance
}

const FirmwareBaseListView: React.FC<Props> = ({ api }) => {
    const canAddFirmwareBase: boolean = useHasAction("firmware_manager_add_firmware_base");
    const canEditFirmwareBase: boolean = useHasAction("firmware_manager_edit_firmware_base");
    const canDownloadFirmwareBase: boolean = useHasAction("firmware_manager_download_firmware_base");

    const {token} = theme.useToken();

    const {
        getValues: firmwareAddGetValues,
        reset: firmwareAddReset,
        control: firmwareAddControl,
        formState: {errors: firmwareAddErrors, isValid: firmwareAddIsValid},
    } = useForm<FirmwareBase>({mode: "onChange"});

    const {
        getValues: firmwareEditGetValues,
        reset: firmwareEditReset,
        control: firmwareEditControl,
        formState: {errors: firmwareEditErrors, isValid: firmwareEditIsValid, isDirty: firmwareEditIsDirty}
    } = useForm<FirmwareBase>({mode: "onChange"});

    const [firmwareBaseList, setFirmwareBase] = useState<any[]>()
    const [showEditFirmwareModal, setEditFirmwareModal] = useState<boolean>(false)
    const [showAddModal, setShowAddModal] = useState<boolean>(false);

    const baseFirmwareListQuery = useQuery({
        queryKey: ['baseFirmwareList'],
        queryFn: async () => {
            const res = await axios.get<FirmwareBaseListResponse>(`${BASE_URL}get_firmware_bases`)
            // console.log('Firmware Base List:', res.data.content)
            return res.data
        }
    })

    const jsonSchemaListQuery = useQuery({
        queryKey: ['jsonSchemaList'],
        queryFn: async () => {
            const res = await axios.get<JsonSchemaListResponse>(BASE_URL + 'get_json_schemas_base')
            // console.log('JSON Schema List:', res.data.content)
            return res.data.content
        }
    })


    const firmwareBaseStatesQuery = useQuery({
        queryKey: ['firmwareBaseStates'],
        queryFn: async () => {
            const res = await axios.get<FirmwareBaseStatesResponse>(`${BASE_URL}get_firmware_bases_states`)
            // console.log('Firmware Base States:', res.data.content)
            return res.data.content.map((version: any) => ({
                value: version,
                label: version
            }));
        }
    })

    useEffect(() => {
        // Fetch firmware_bases devices from api
        setFirmwareBase(getApplicationList(baseFirmwareListQuery.data));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseFirmwareListQuery.data]);

    const toggleAddFirmwareModal = () => {
        setShowAddModal(!showAddModal)
        firmwareAddReset()
    }

    const toggleEditFirmwareModal = () => {
        setEditFirmwareModal(!showEditFirmwareModal)
    }

    const getDescriptions: (data: FirmwareBase) => DescriptionsProps['items'] = (data) => {
        var out_data: DescriptionsProps['items'] = [
            {
                label: "Commit hash",
                children: <Text code>{data.commit_hash}</Text>
            },
            {
                label: "Changelog",
                children: renderChangelog(data.changelog)
            },
            {
                label: "Status",
                children: <Tag color={getStatusColor(data.status)}>{data.status}</Tag>

            },
            {
                label: "JSON SCHEMA VERSION",
                children: data.json_schema_base.version_schema_name
            },
            {
                label: 'Download binaries',
                children: (
                    <Space wrap>
                        <Button icon={<DownloadOutlined />} onClick={() => download(data, "app", "s37", api)} disabled={!canDownloadFirmwareBase}>App (.s37)</Button>
                        <Button icon={<DownloadOutlined />} onClick={() => download(data, "app_gbl", "gbl", api)} disabled={!canDownloadFirmwareBase}>App GBL (.gbl)</Button>
                        <Button icon={<DownloadOutlined />} onClick={() => download(data, "loader", "s37", api)} disabled={!canDownloadFirmwareBase}>Loader (.s37)</Button>
                    </Space>
                )
            },
        ];
        return out_data;
    }

    const getApplicationList = (data: FirmwareBaseListResponse | undefined): CollapseProps['items'] => {
        const panelStyle: React.CSSProperties = {
            marginBottom: 24,
            background: token.colorFillAlter,
            borderRadius: token.borderRadiusLG,
            border: 'none',
            width: '100%'
        };

        if (!data?.success) return [];

        const grouped: Record<string, FirmwareBase[]> = {};

        data.content.forEach(firmware => {
            const match = firmware.version_name.match(/^(\d+\.\d+)/);
            const groupKey = match ? match[1] : "Other";

            if (!grouped[groupKey]) {
                grouped[groupKey] = [];
            }
            grouped[groupKey].push(firmware);
        });

        const sortedGroupKeys = Object.keys(grouped).sort((a, b) => {
            const [aMajor, aMinor] = a.split('.').map(Number);
            const [bMajor, bMinor] = b.split('.').map(Number);
            if (bMajor !== aMajor) return bMajor - aMajor;
            return bMinor - aMinor;
        });

        const out_data: CollapseProps['items'] = sortedGroupKeys.map((groupKey, groupIndex) => {
            grouped[groupKey].sort((a, b) => new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime());

            return {
                key: `group-${groupIndex}`,
                label: `Version ${groupKey}`,
                children:(
                    <Collapse
                        bordered={false}
                        items={grouped[groupKey].map((firmwareData, index) => ({
                            key: `${groupIndex}-${index}`,
                            label: (
                                <Space>
                                    <Text strong>{firmwareData.version_name}</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{String(firmwareData.created_at)}</Text>
                                    <Tag color={getStatusColor(firmwareData.status)}>{firmwareData.status}</Tag>
                                </Space>
                            ),
                            children: (
                                <Descriptions
                                    layout="vertical"
                                    column={{xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1}}
                                    bordered
                                    items={getDescriptions(firmwareData)}
                                />
                            ),
                            style: panelStyle,
                            extra: (
                                <Tooltip title="Edit Firmware">
                                    <Button
                                        icon={<EditOutlined/>}
                                        onClick={e => {
                                            e.stopPropagation();
                                            toggleEditFirmwareModal();
                                            firmwareEditReset(firmwareData);
                                        }}
                                        disabled={!canEditFirmwareBase}
                                    />
                                </Tooltip>
                            )
                        }))}
                    />
                ),
                style:panelStyle,
            }
        });
        return out_data;
    }

    return (
        <div>
            <Card
                title={
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        Firmware Base List
                        <Tooltip title="Add New Firmware">
                            <Button type="primary" shape="circle" icon={<PlusOutlined/>} onClick={toggleAddFirmwareModal} disabled={!canAddFirmwareBase}/>
                        </Tooltip>
                    </div>
                }
                style={cardStyle}
                loading={baseFirmwareListQuery.isPending}
            >
                {
                    baseFirmwareListQuery.error
                        ? <>{baseFirmwareListQuery.error.message}</>
                        : <Collapse
                            collapsible="icon"
                            bordered={false}
                            items={firmwareBaseList}
                            style={{background: token.colorBgContainer}}/>
                }
                <EditFirmwareBaseListModal
                    showEditFirmwareModal={showEditFirmwareModal}
                    firmwareBaseStatesQuery={firmwareBaseStatesQuery}
                    toggleEditFirmwareModal={toggleEditFirmwareModal}
                    firmwareEditGetValues={firmwareEditGetValues}
                    firmwareEditReset={firmwareEditReset}
                    firmwareEditControl={firmwareEditControl}
                    api={api}
                    isValid={firmwareEditIsValid}
                    isDirty={firmwareEditIsDirty}
                    errors={firmwareEditErrors}
                />
                <AddFirmwareBaseListModal
                    showAddModal={showAddModal}
                    jsonSchemaListQuery={jsonSchemaListQuery}
                    toggleAddFirmwareModal={toggleAddFirmwareModal}
                    firmwareAddErrors={firmwareAddErrors}
                    firmwareAddControl={firmwareAddControl}
                    firmwareAddGetValues={firmwareAddGetValues}
                    api={api}
                    isValid={firmwareAddIsValid}

                />
            </Card>
        </div>
    )
};

export default FirmwareBaseListView;
